import { forwardRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { motion } from 'framer-motion';
import { Plus, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { DraggableInterest } from './DraggableInterest';

export const MatrixGrid = forwardRef(({ interests, triggeredReminders, onDragEnd, onEdit, onAchieve, onAddClick }, ref) => {
  const { theme } = useTheme();

  const quadrants = [
    {
      key: 'q1', position: 'top-2 left-2', align: 'start', side: 'bottom',
      label: 'High Attention / High Impact', color: 'emerald',
      tooltip: 'What actions can you take to move interests in and out of this zone?',
      gradient: theme === 'dark'
        ? 'radial-gradient(circle at top left, rgba(16, 185, 129, 0.35), transparent 70%)'
        : 'radial-gradient(circle at top left, rgba(16, 185, 129, 0.4), transparent 70%)',
      iconLeft: false
    },
    {
      key: 'q2', position: 'top-2 right-2', align: 'end', side: 'bottom',
      label: 'High Attention / Low Impact', color: 'amber',
      tooltip: 'What actions can you take to reduce your attention to these interests?',
      gradient: theme === 'dark'
        ? 'radial-gradient(circle at top right, rgba(245, 158, 11, 0.35), transparent 70%)'
        : 'radial-gradient(circle at top right, rgba(245, 158, 11, 0.4), transparent 70%)',
      iconLeft: true
    },
    {
      key: 'q3', position: 'bottom-2 left-2', align: 'start', side: 'top',
      label: 'Low Attention / High Impact', color: 'amber',
      tooltip: 'What actions should you take to move interests out of this zone?',
      gradient: theme === 'dark'
        ? 'radial-gradient(circle at bottom left, rgba(245, 158, 11, 0.35), transparent 70%)'
        : 'radial-gradient(circle at bottom left, rgba(245, 158, 11, 0.4), transparent 70%)',
      iconLeft: false
    },
    {
      key: 'q4', position: 'bottom-2 right-2', align: 'end', side: 'top',
      label: 'Low Attention / Low Impact', color: 'rose',
      tooltip: 'What actions can you take to resolve these interests?',
      gradient: theme === 'dark'
        ? 'radial-gradient(circle at bottom right, rgba(244, 63, 94, 0.35), transparent 70%)'
        : 'radial-gradient(circle at bottom right, rgba(244, 63, 94, 0.4), transparent 70%)',
      iconLeft: true
    }
  ];

  const colorMap = {
    emerald: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
    rose: 'text-rose-600 dark:text-rose-400'
  };

  return (
    <main className="flex-1 p-2 md:p-4 relative overflow-hidden">
      <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center">
        <span className="text-sm md:text-base font-bold text-primary tracking-wide" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>ATTENTION</span>
      </div>
      
      <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 ml-3">
        <span className="text-sm md:text-base font-bold text-primary tracking-wide">IMPACT</span>
      </div>

      <div 
        ref={ref}
        className="relative w-[calc(100%-2rem)] h-[calc(100vh-7rem)] ml-8 mt-6 rounded-2xl overflow-hidden"
        style={{
          background: theme === 'dark' 
            ? 'radial-gradient(circle at center, #2a2a2f 0%, #1a1d21 100%)'
            : 'radial-gradient(circle at center, #ffffff 0%, #fdfbf7 100%)'
        }}
      >
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 pointer-events-none">
          {quadrants.map(q => (
            <div key={q.key} className="relative" style={{ background: q.gradient }}>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      className={`absolute ${q.position} flex items-center gap-1 text-[10px] md:text-xs font-medium ${colorMap[q.color]} cursor-help pointer-events-auto bg-transparent border-none p-0 hover:opacity-80 transition-opacity`}
                      data-testid={`${q.key}-label`}
                    >
                      {q.iconLeft && <Info className="w-3 h-3 opacity-60" />}
                      {q.label}
                      {!q.iconLeft && <Info className="w-3 h-3 opacity-60" />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side={q.side} align={q.align} className="max-w-[240px] text-center">
                    <p>{q.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          ))}
        </div>

        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border/50" />
          <div className="absolute top-1/2 left-0 right-0 h-px bg-border/50" />
        </div>

        <div className="absolute inset-0 pointer-events-none">
          {interests.map(interest => (
            <DraggableInterest
              key={interest.interest_id}
              interest={interest}
              onDragEnd={onDragEnd}
              onEdit={() => onEdit(interest)}
              onAchieve={() => onAchieve(interest)}
              containerRef={ref}
              allInterests={interests}
              reminderTriggered={triggeredReminders.has(interest.interest_id)}
            />
          ))}
        </div>

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={onAddClick}
            className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center ring-2 ring-background/80"
            data-testid="add-interest-btn"
          >
            <Plus className="w-5 h-5" />
          </motion.button>
          <p className="text-center text-[8px] md:text-[10px] text-muted-foreground mt-1 whitespace-nowrap">
            Add interest
          </p>
        </div>
      </div>
    </main>
  );
});

MatrixGrid.displayName = 'MatrixGrid';
